<template>
  <TopNav />
  <div class="container">
    <LeftNav />
    <div class="email-middle">
      <!-- Category Selection -->
       <div v-if="selectedInbox.failed" class="sync-failed">
          Email Sync Failed, The existing Tokens have expired, please refresh them by <button class="primary" @click.prevent="this.$router.push('/connections')">Clicking Here</button>
       </div>
      <div class="category-selection" ref="categorySelection">
        <div class="d-flex">
          <div
            @click="activeCategory = ''"
            data-category="All"
            class="category-button"
            :style="!activeCategory ? { color: '#42c88d' } : {}"
          >
            All
          </div>
          <div
            v-for="(color, category, index) in emailCategoryColors"
            :key="index"
            @click="activeCategory = category"
            class="category-button"
            :data-category="category"
            :class="{
              'active-category': activeCategory === category,
              'previous-category': transitioning && category === previousCategory
            }"
            :style="activeCategory === category ? { color: '#42c88d' } : {}"
          >
            {{ category }}
          </div>
        </div>
        <!-- Slide indicator -->
        <div class="slide-indicator" :style="slideIndicatorStyle"></div>
      </div>

      <div class="bottom-section">
        <!-- Left Panel: Thread List -->
        <div class="left-panel">
          <div class="filter-div">
            <input type="text" placeholder="Search" v-model="filterEmails" />
          </div>
          <div
            v-for="(thread, index) in filteredThreads"
            :key="index"
            :class="thread.thread_id === activeThread.thread_id ? 'preview-div-active' : 'preview-div'"
            @click.prevent.stop="setActiveThread(thread)"
          >
            <div class="d-flex justify-content-between w-100 align-items-center">
              <span class="sender">
                {{ getSenderName(lastEmail(thread.emails).from) }}
              </span>
              <span class="sent-time">
                {{ fomatTime(lastEmail(thread.emails).received_at) }}
              </span>
            </div>
            <span class="subject">{{ lastEmail(thread.emails).subject }}</span>
            <span class="preview" v-html="cleanPreview(lastEmail(thread.emails).snippet)"></span>
            <div
              v-if="!activeCategory"
              class="category-badge"
              :style="{
                borderColor: emailCategoryColors[lastEmail(thread.emails).category || 'Uncategorized'],
                color: emailCategoryColors[lastEmail(thread.emails).category || 'Uncategorized']
              }"
            >
              {{ lastEmail(thread.emails).category || "Uncategorized" }}
            </div>
          </div>
        </div>

        <!-- Right Panel: Email Details -->
        <div class="right-panel">
          <div class="email-actions">
            <div class="left">
              <ReplyIcon @click.prevent.stop="actionClicked('reply')" width="20px" class="cursor-pointer" />
              <ReplyAllIcon @click.prevent.stop="actionClicked('reply-all')" width="20px" class="cursor-pointer" />
              <ReplyIcon @click.prevent.stop="actionClicked('forward')" class="forward-icon cursor-pointer" width="20px" />
              <TrashIcon class="cursor-pointer" />
              <ArchiveIcon width="20px" class="cursor-pointer" />
            </div>
            <div class="right">
              <button class="primary" @click.prevent.stop="createTasksClicked()">Create Tasks</button>
            </div>
          </div>

          <!-- Email Draft Form -->
          <div v-if="showEmailDraft" class="d-flex flex-direction-column gap-5">
            <CustomTagsInput
              v-model="newEmail.to"
              :validator="(tag) => emailValidator(tag)"
              errorMessage="Please enter a valid email"
              placeholder="To Email"
            />
            <CustomTagsInput
              v-model="newEmail.cc"
              :validator="(tag) => emailValidator(tag)"
              errorMessage="Please enter a valid email"
              placeholder="CC Email"
            />
            <input placeholder="Subject" v-model="newEmail.subject" />
            <input placeholder="from" :value="selectedInbox.account_name" disabled="disabled" />
            <div class="quill-wrapper">
              <quillEditor v-model:value="newEmail.body" ref="quillEditor" />
            </div>
            <div class="d-flex justify-content-between">
              <button @click.prevent.stop="draftResponse()" class="secondary-invert generate-response">
                <LoadingSpinner v-if="generatingResponse"/>
                <span v-else>AI Draft Response</span>
              </button>
              <div class="d-flex gap-5">
                <button class="secondary-invert" @click.prevent.stop="showEmailDraft = false">Cancel</button>
                <button class="primary" @click.prevent.stop="sendEmail()">Send</button>
              </div>
            </div>
          </div>

          <!-- AI Summary -->
          <div
            v-if="activeThread && activeThread.emails && lastEmail(activeThread.emails).ai_summary"
            class="ai-summary"
          >
            <blockquote>
              <p class="quote-text">{{ lastEmail(activeThread.emails).ai_summary }}</p>
              - AI Summary
            </blockquote>
          </div>

          <!-- Loop through emails in the active thread -->
          <div class="iframe-container" v-if="activeThread && activeThread.emails && activeThread.emails.length">
            <div v-for="(email, idx) in activeThread.emails" :key="idx" class="email-section">
              <!-- Email Header using category color -->
              <div class="email-header">
                <div class="d-flex flex-direction-column gap-1-rem">
                  <span class="from">{{ email.from }}</span>
                  <span class="subject">{{ email.subject }}</span>
                </div>
              
                <span class="sent-time">{{ fomatTime(email.received_at) }}</span>
              </div>
              <iframe
                class="email-frame"
                :srcdoc="purifyHtmlForEmail(email)"
                @load="resizeIframe($event)"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LeftNav from '@/components/LeftNav.vue';
import TopNav from '@/components/TopNav.vue';
import { mapGetters } from 'vuex';
import DOMPurify from 'dompurify';
import TrashIcon from '@/components/icons/TrashIcon.vue';
import ArchiveIcon from '@/components/icons/ArchiveIcon.vue';
import ReplyIcon from '@/components/icons/ReplyIcon.vue';
import ReplyAllIcon from '@/components/icons/ReplyAllIcon.vue';
import { quillEditor } from 'vue3-quill';
import "@/assets/quill-dark-theme.css";
import CustomTagsInput from '@/components/CustomTagsInput.vue';
import LoadingSpinner from '@/components/icons/LoadingSpinner.vue';
window.MutationObserver = window.MutationObserver || window.WebKitMutationObserver;

export default {
  components: { 
    LeftNav, 
    TopNav, 
    TrashIcon, 
    ArchiveIcon, 
    ReplyIcon, 
    ReplyAllIcon, 
    quillEditor,
    CustomTagsInput,
    LoadingSpinner
  },
  computed: {
    ...mapGetters(['selectedInbox', 'connections', 'emails']),
    filteredThreads() {
      if (!this.selectedInbox || !this.emails[this.selectedInbox.account_name]) {
        return [];
      }
      // Parse each thread's emails JSON and set defaults.
      let threads = this.emails[this.selectedInbox.account_name].map(thread => {
        let parsedEmails = [];
        try {
          parsedEmails = typeof thread.emails === "string" ? JSON.parse(thread.emails) : thread.emails;
        } catch (e) {
          console.error("Error parsing emails for thread", thread.thread_id, e);
        }
        parsedEmails = parsedEmails.map(email => ({
          ...email,
          from: email.from ? email.from : "Unknown Sender",
          category: (email.category !== undefined && email.category !== null) ? email.category : "Uncategorized"
        }));
        return {
          thread_id: thread.thread_id,
          emails: parsedEmails
        };
      });
      let validThreads = threads.filter(thread => thread.emails && thread.emails.length);
      
      if (this.filterEmails) {
        const search = this.filterEmails.toLowerCase();
        validThreads = validThreads.filter(thread => {
          const latest = this.lastEmail(thread.emails);
          return (
            (latest.subject && latest.subject.toLowerCase().includes(search)) ||
            (latest.snippet && latest.snippet.toLowerCase().includes(search)) ||
            (latest.from && latest.from.toLowerCase().includes(search)) ||
            (latest.to && latest.to.toLowerCase().includes(search))
          );
        });
      }
      if (this.activeCategory) {
        validThreads = validThreads.filter(thread => {
          const latest = this.lastEmail(thread.emails);
          const emailCategory = (latest.category !== undefined && latest.category !== null) ? latest.category : "Uncategorized";
          return emailCategory === this.activeCategory;
        });
      }
      return validThreads;
    }
  },
  data() {
    return {
      activeThread: null, // { thread_id, emails: [] }
      showEmailDraft: false,
      newEmail: { to: [], cc: [], subject: '', body: '',thread_id: null },
      emailCategoryColors: {
        "Personal": "#4CAF50",
        "Work/Professional": "#2196F3",
        "Newsletters/Subscriptions": "#FF9800",
        "Marketing/Promotions": "#FF4081",
        "Finance/Billing": "#9C27B0",
        "Support/Customer Service": "#00BCD4",
        "Security/Alerts": "#F44336",
        "Spam/Junk": "#9E9E9E",
        "Automated Notifications": "#607D8B",
        "Networking/Recruitment": "#6575d1",
        "Legal/Compliance": "#795548",
        "Tasks/Reminders": "#8BC34A",
        "Urgent": "#E91E63",
        "Uncategorized": "#CCCCCC",
        "No Category": "#CCCCCC"
      },
      activeCategory: "",
      previousCategory: "",
      transitioning: false,
      slideIndicatorStyle: {},
      filterEmails: "",
      debouncedFetch: null,
      generatingResponse: false
    };
  },
  mounted() {
    this.updateSlideIndicator();
  },
  watch: {
    filterEmails(newVal) {
      this.debouncedFetch(newVal);
    },
    activeCategory(newCategory, oldCategory) {
      if (newCategory !== oldCategory) {
        this.previousCategory = oldCategory;
        this.transitioning = true;
        setTimeout(() => { this.transitioning = false; }, 400);
      }
      this.updateSlideIndicator();
      this.setActiveThread(this.filteredThreads.length > 0 ? this.filteredThreads[0] : null);
    },
    connections: {
      deep: true,
      handler() {
        if (!this.selectedInbox) {
          this.$store.dispatch('setDefaultInbox');
        }
      }
    },
    selectedInbox: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        this.$watch(
          () => this.emails[newVal.account_name],
          (newThreads) => {
            if (newThreads && newThreads.length > 0) {
              console.log('Selecting first thread:', newThreads[0]);
              this.setActiveThread(this.filteredThreads.length > 0 ? this.filteredThreads[0] : null);
            }
          },
          { immediate: true }
        );
      }
    }
  },
  created() {
    this.debouncedFetch = this.debounce((query) => {
      this.fetchEmails(query);
    }, 250);
  },
  methods: {
    createTasksClicked(){

    },
    resetNewEmail(){
      this.newEmail = { to: [], cc: [], subject: '', body: '', thread_id: null};
    },
    async sendEmail() {
      let sentEmail = await this.$store.dispatch('sendEmail', this.newEmail);
      console.log('starting');
      console.log(this.activeThread);
      console.log(sentEmail);
      this.activeThread.emails.unshift(sentEmail);
      this.showEmailDraft = false;
      this.resetNewEmail();
    },
    async draftResponse(){
      this.generatingResponse = true;
      let response = await this.$store.dispatch('draftResponse', this.activeThread.thread_id);
      console.log('wagwan');
      console.log(response);
      this.generatingResponse = false;
      this.newEmail.body = response;
    },
    fetchEmails(query) {
      this.$store.dispatch('getInboxEmails', query);
    },
    debounce(func, delay) {
      let timer;
      return function(...args) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },
    emailValidator(tag) {
      const match = tag.match(/<([^>]+)>/);
      const email = match ? match[1] : tag;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    actionClicked(action) {
      this.showEmailDraft = true;
      this.resetNewEmail();
      if (!this.activeThread || !this.activeThread.emails.length){
        return;
      } 
      const latest = this.lastEmail(this.activeThread.emails);
      this.newEmail.thread_id = this.activeThread.thread_id;
      if (latest.subject) {
        this.newEmail.subject = "Re: " + latest.subject;
      }
      if (action === 'reply' || action === 'reply-all') {
        this.newEmail.to.push(latest.from);
      }
      if (action === 'reply-all' && latest.cc) {
        this.newEmail.cc.push(latest.cc);
      }
    },
    purifyHtmlForEmail(email) {
  let rawBody = email.html_body || email.body || email.snippet;
  if (!rawBody) return '';

  // Detect full HTML if there's a <html tag anywhere
  const isFullHtml = /<html/i.test(rawBody);

  // Sanitize using an extended config for full HTML emails
  let sanitizedBody = isFullHtml
      ? DOMPurify.sanitize(rawBody, {
          ADD_TAGS: ['html', 'head', 'body', 'meta', 'link', 'title', 'style']
        })
      : DOMPurify.sanitize(rawBody);

    // Process quoted text if present (only for non-full HTML emails)
    const replyRegex = /(On\s\w{3,9}\s\d{1,2},\s\d{4},\sat\s\d{1,2}:\d{2}\s(?:AM|PM),.*wrote:)/i;
    const hasQuotedHeader = replyRegex.test(sanitizedBody);
    if (hasQuotedHeader && !isFullHtml) {
      sanitizedBody = sanitizedBody
        .replace(replyRegex, `<p class="quoted-header"><strong>$1</strong></p>`)
        .replace(/^&gt;\s?(.*)$/gm, '<blockquote class="quoted-text">$1</blockquote>');
    }

    // Determine if it's a simple text email
    const isSimpleText = !/<(div|table|img|iframe|video|ul|ol|style|script|head|body|html)>/i.test(sanitizedBody);

    // Only apply custom styles for simple/quoted emails, not full HTML ones
    if (!isFullHtml && (isSimpleText || hasQuotedHeader)) {
      const rootStyles = getComputedStyle(document.documentElement);
      const bgColor = rootStyles.getPropertyValue('--Border-Color-Primary').trim();
      const textColor = rootStyles.getPropertyValue('--white-color').trim();

      sanitizedBody = `
        <html>
          <head>
            <style>
              body {
                color: ${textColor};
                font-family: sans-serif;
                background-color: ${bgColor};
              }
              .quoted-header {
                color: #b487ff;
                font-weight: bold;
                margin-left: 10px;
              }
              .quoted-text {
                color: #c6a2ff;
                padding-left: 10px;
                border-left: 2px solid purple;
                display: block;
              }
            </style>
          </head>
          <body>${sanitizedBody}</body>
        </html>
      `;
    }

    // Parse the sanitized HTML and update all anchor tags to open in a new tab
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedBody, 'text/html');
    doc.querySelectorAll('a').forEach(link => {
      link.setAttribute('target', '_blank');
    });
    return doc.documentElement.outerHTML;
},
resizeIframe(event) {
  const iframe = event.target;

  function adjust() {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      // Get the content height from the body
      const contentHeight = doc.body.scrollHeight;
      // Add some padding if needed
      const newHeight = contentHeight + 40;
      const maxHeight = window.innerHeight * 0.8; // 80% of viewport
      // Clamp the height to the max value
      iframe.style.height = Math.min(newHeight, maxHeight) + 'px';
      iframe.style.overflowY = newHeight > maxHeight ? 'auto' : 'hidden';
    } catch (e) {
      console.error('Error adjusting iframe:', e);
    }
  }

  // Adjust multiple times to catch late reflows
  adjust();
  setTimeout(adjust, 100);
  setTimeout(adjust, 300);
  setTimeout(adjust, 600);
}
,
  setActiveThread(thread) {
    this.showEmailDraft = false;
    this.resetNewEmail();
    this.activeThread = thread;
  },
  fomatTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();

    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (date.toDateString() === now.toDateString()) {
      return timeString; // Show only time for today's messages
    }

    const dateString = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return `${dateString} ${timeString}`; // Show date + time for non-today messages
  },
  cleanPreview(preview) {
    return preview.replace(/\u200C/g, '');
  },
  getSenderName(sender) {
    if (sender) {
      const match = sender.match(/"?(.*?)"?\s*<.*?>/);
      return match ? match[1] : sender;
    }
    return "Unknown Sender";
  },
  lastEmail(emailsArray) {
    return emailsArray && emailsArray.length ? emailsArray[emailsArray.length - 1] : {};
  },
  getCategory(email) {
    return email && email.category && email.category.trim() !== ""
      ? email.category
      : "No Category";
  },
  updateSlideIndicator() {
    this.$nextTick(() => {
      const container = this.$refs.categorySelection;
      if (!container) return;
      const activeCategoryName = this.activeCategory === '' ? 'All' : this.activeCategory;
      const activeElement = container.querySelector(`[data-category="${activeCategoryName}"]`);
      if (activeElement) {
        this.slideIndicatorStyle = {
          left: activeElement.offsetLeft + 'px',
          width: activeElement.offsetWidth + 'px'
        };
      }
    });
  }
  }
};
</script>

<style scoped>

.generate-response{
  width: 175px;
}
.sync-failed{
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  gap: 1rem;
  background-color: rgb(196, 10, 10);
  border-radius: 5px;
  padding: 1rem;
}
.filter-div {
  margin-bottom: 10px;
}
.iframe-container {
  background-color: transparent;
  /* Remove fixed height so it grows with content */
  height: auto;
  display: flex; 
  flex-direction: column;
  height: 80vh;
  overflow-y: auto;
  margin-bottom: 20px;
}
.ai-summary {
  text-align: center;
  margin: 20px 0;
}
.ai-summary blockquote {
  display: inline-block;
  width: 100%;
  padding: 1em;
  background-color: var(--Border-Color-Primary);
  border-left: 5px solid var(--primary-color);
  font-style: italic;
  color: var(--primary-color);
  text-align: center;
  position: relative;
}
.ai-summary .quote-text {
  display: inline-block;
  font-size: 1.2em;
  line-height: 0.5;
  position: relative;
}
.ai-summary .quote-text::before {
  content: "“";
  font-size: 2.5em;
  vertical-align: middle;
  margin-right: 0.2em;
}
.ai-summary .quote-text::after {
  content: "”";
  font-size: 2.5em;
  vertical-align: middle;
}
::v-deep .v3ti .v3ti-tag {
  color: white;
  background-color: var(--secondary-color);
}
::v-deep .v3ti .v3ti-tag .v3ti-remove-tag {
  color: white;
  opacity: 80%;
}
.v3ti {
  display: block;
  width: 100%;
  color: var(--white-color);
  background-color: var(--black-colo-08);
  border: 1px solid var(--Border-Color-Primary);
  border-radius: 5px;
  padding: 10px;
  font-size: 1rem;
}
@keyframes slideHighlight {
  from { background-color: rgba(66, 200, 141, 0.2); }
  to { background-color: transparent; }
}
.previous-category {
  animation: slideHighlight 0.4s ease-in-out;
  color: rgba(66, 200, 141, 0.5);
  transition: color 0.4s ease-in-out;
}
.category-button {
  padding: 20px;
  cursor: pointer;
}
.bottom-section {
  display: flex;
}
.category-selection {
  display: flex;
  position: relative;
}
.category-badge {
  padding: 2px 4px;
  border-radius: 5px;
  width: fit-content;
  border: 1px solid;
}
.email-actions .left {
  display: flex;
  gap: 10px;
}
.email-actions .right {
  display: flex;
  gap: 1rem;
}
.quill-wrapper {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
}
.forward-icon {
  transform: scaleX(-1);
}
.email-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}
.container {
  display: flex;
  height: calc(100vh - 80px);
}
.sent-time { font-size: 1rem; color: var(--dark-color-6b); }
.sender { font-size: 1rem; }
.subject { font-size: 1rem; }
.preview { font-size: 1rem; color: var(--dark-color-6b); }
.preview-div {
  padding: 1rem;
  border-bottom: 1px solid var(--gray-color-2c);
  display: flex;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}
.preview-div-active {
  padding: 1rem;
  border-bottom: 1px solid var(--gray-color-2c);
  background-color: var(--gray-color-2c);
  display: flex;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}
.left-panel {
  display: flex;
  flex-direction: column;
  background-color: var(--Theme-Color-Secondary-Black);
  padding: 1rem;
  width: 20%;
  overflow-y: auto;
  height: 87vh;
}
.right-panel {
  display: flex;
  flex-direction: column;
  background-color: var(--Theme-Color-Secondary-Black);
  padding: 1rem;
  width: 80%;
}
.email-middle {
  flex-grow: 1;
  margin-left: 300px;
  display: flex;
  padding: 3rem;
  flex-direction: column;
}
.email-frame {
  width: 100%;
  height: auto;
  border: none;
  display: block;
  background-color: white;
}
.left-panel::-webkit-scrollbar {
  width: 8px;
}
.left-panel::-webkit-scrollbar-track {
  background: #1b1b1b;
  border-radius: 8px;
}
.left-panel::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 8px;
}
.left-panel::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.iframe-container::-webkit-scrollbar {
  width: 8px;
}
.iframe-container::-webkit-scrollbar-track {
  background: #1b1b1b;
  border-radius: 8px;
}
.iframe-container::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 8px;
}
.iframe-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.email-frame::-webkit-scrollbar {
  width: 8px;
}
.email-frame::-webkit-scrollbar-track {
  background: #1b1b1b;
  border-radius: 8px;
}
.email-frame::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 8px;
}
.email-frame::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.slide-indicator {
  position: absolute;
  bottom: 0;
  height: 2px;
  background-color: #42c88d;
  transition: left 0.4s ease, width 0.4s ease;
}
.email-section {
  margin-bottom: 1rem;
  /* Allow the section to grow with its content */
  height: auto;
  background-color: var(--Border-Color-Primary);
  margin-bottom: 20px;
}
.email-header {
  padding: 1rem;
  border-bottom: 1px solid var(--black-colo-08);
  color: var(--white-color);
  display: flex;
  justify-content: space-between;
}
.email-header .from{
  font-size: 18px;
}
</style>
